import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Grid, TextField, Switch, FormControlLabel, Button, Alert,
  Checkbox, Divider, InputAdornment, Skeleton,
} from '@mui/material';
import api from '../config/api';

const ff = "'Poppins', sans-serif";
const DOC_OPTIONS = [
  { key: 'ID_FRONT', label: 'ID front' },
  { key: 'ID_BACK', label: 'ID back' },
  { key: 'SELFIE', label: 'Selfie' },
];

// The app/back end store money in cents; the form edits dollars.
const toDollars = (c) => (c == null ? '' : (c / 100).toString());
const toCents = (d) => Math.round((parseFloat(d) || 0) * 100);

const Field = ({ label, children, help }) => (
  <Box>
    <Box sx={{ fontFamily: ff, fontSize: 12, fontWeight: 600, color: '#4A5568', mb: 0.5 }}>{label}</Box>
    {children}
    {help && <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0', mt: 0.5 }}>{help}</Box>}
  </Box>
);

export default function PayoutSettings() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/payout-settings');
        setS((res.data.data || res.data).settings);
      } catch (e) { setError('Failed to load settings.'); }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (patch) => { setS((prev) => ({ ...prev, ...patch })); setSaved(false); };
  const toggleDoc = (key) => {
    const cur = s.requiredDocs || [];
    set({ requiredDocs: cur.includes(key) ? cur.filter((d) => d !== key) : [...cur, key] });
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const res = await api.put('/admin/payout-settings', s);
      setS((res.data.data || res.data).settings);
      setSaved(true);
    } catch (e) { setError(e.response?.data?.message || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  if (loading || !s) {
    return <Box sx={{ p: 1 }}><Skeleton variant="rounded" height={420} /></Box>;
  }

  // Live example: a $100 withdrawal with the current settings.
  const exGross = 10000;
  const exFlat = toCents(toDollars(s.flatFeeCents)) || 0;
  const exPct = Math.round(exGross * ((Number(s.feePercent) || 0) / 100));
  const exNet = Math.max(0, exGross - exFlat - exPct);
  const m = (c) => `$${(c / 100).toFixed(2)}`;

  return (
    <Box sx={{ fontFamily: ff, maxWidth: 860 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Settings saved.</Alert>}

      <Paper sx={{ p: 3, borderRadius: '16px', mb: 2 }}>
        <FormControlLabel
          control={<Switch checked={!!s.enabled} onChange={(e) => set({ enabled: e.target.checked })} />}
          label={<Box sx={{ fontFamily: ff, fontWeight: 600 }}>Cash-out {s.enabled ? 'enabled' : 'disabled'}</Box>}
        />
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <Field label="Conversion rate" help="Coins that equal $1.">
              <TextField fullWidth size="small" type="number" value={s.coinsPerDollar}
                onChange={(e) => set({ coinsPerDollar: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">coins = $1</InputAdornment> }} />
            </Field>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="Minimum withdrawal">
              <TextField fullWidth size="small" type="number" value={toDollars(s.minWithdrawalCents)}
                onChange={(e) => set({ minWithdrawalCents: toCents(e.target.value) })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            </Field>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="Flat fee" help="Charged per withdrawal.">
              <TextField fullWidth size="small" type="number" value={toDollars(s.flatFeeCents)}
                onChange={(e) => set({ flatFeeCents: toCents(e.target.value) })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            </Field>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="Percentage fee">
              <TextField fullWidth size="small" type="number" value={s.feePercent}
                onChange={(e) => set({ feePercent: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
            </Field>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="Earned-coin hold" help="Days before earned coins become cashable.">
              <TextField fullWidth size="small" type="number" value={s.holdDays}
                onChange={(e) => set({ holdDays: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }} />
            </Field>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="Required documents">
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {DOC_OPTIONS.map((d) => (
                  <FormControlLabel key={d.key}
                    control={<Checkbox size="small" checked={(s.requiredDocs || []).includes(d.key)} onChange={() => toggleDoc(d.key)} />}
                    label={<Box sx={{ fontFamily: ff, fontSize: 13 }}>{d.label}</Box>} />
                ))}
              </Box>
            </Field>
          </Grid>
          <Grid item xs={12}>
            <Field label="Processing-time message" help="Shown to sitters on the Cash Out screen.">
              <TextField fullWidth size="small" value={s.processingText}
                onChange={(e) => set({ processingText: e.target.value })} />
            </Field>
          </Grid>
        </Grid>
      </Paper>

      {/* Live example */}
      <Paper sx={{ p: 3, borderRadius: '16px', mb: 2, bgcolor: '#F7FAFC' }}>
        <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: 700, color: '#1A202C', mb: 1 }}>Example — sitter withdraws $100</Box>
        <Row label="Amount" value={m(exGross)} />
        <Row label={`Flat fee`} value={`– ${m(exFlat)}`} red />
        <Row label={`Percentage fee (${s.feePercent || 0}%)`} value={`– ${m(exPct)}`} red />
        <Divider sx={{ my: 1 }} />
        <Row label="Sitter receives" value={m(exNet)} bold />
      </Paper>

      <Button variant="contained" onClick={save} disabled={saving}
        sx={{ fontFamily: ff, textTransform: 'none', bgcolor: '#32A6D8', px: 4 }}>
        {saving ? 'Saving…' : 'Save settings'}
      </Button>
    </Box>
  );
}

const Row = ({ label, value, red, bold }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
    <Box sx={{ fontFamily: ff, fontSize: 13, color: bold ? '#1A202C' : '#4A5568', fontWeight: bold ? 700 : 400 }}>{label}</Box>
    <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: bold ? 700 : 500, color: red ? '#C53030' : (bold ? '#2E9E5B' : '#1A202C') }}>{value}</Box>
  </Box>
);
